/**
 * Upgrade button (free users) + Manage billing button (subscribers).
 * Both POST to backend which returns a Stripe-hosted URL we redirect to.
 */
import { useState } from 'react';
import type { AuthUser } from '../hooks/useAuth';

interface BillingButtonProps {
  user: AuthUser;
}

function readCookie(name: string): string | null {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

async function postBilling(path: string, body?: unknown): Promise<{ url: string }> {
  const csrf = readCookie('aiprompt_csrf');
  const res = await fetch('/api/billing' + path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(json?.error?.message || `HTTP ${res.status}`);
  return json;
}

export function BillingButton({ user }: BillingButtonProps) {
  const [busy, setBusy] = useState<null | 'monthly' | 'yearly' | 'portal'>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upgrade(interval: 'month' | 'year') {
    setError(null);
    setBusy(interval === 'month' ? 'monthly' : 'yearly');
    try {
      const { url } = await postBilling('/checkout', { interval });
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(null);
    }
  }

  async function manage() {
    setError(null);
    setBusy('portal');
    try {
      const { url } = await postBilling('/portal');
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(null);
    }
  }

  // Lifetime-free users: no upgrade nag, no manage-billing affordance.
  // A quiet status pill replaces both. This branches BEFORE the
  // SUBSCRIBER/ADMIN check so a user who paid AND was later granted
  // lifetime still sees the Lifetime pill rather than Manage billing.
  if (user.lifetimeFree) {
    return (
      <span
        className="lifetime-pill"
        title="Lifetime account — all Subscriber features unlocked, no billing"
      >
        Lifetime
      </span>
    );
  }

  if (user.role === 'SUBSCRIBER' || user.role === 'ADMIN') {
    // Admins normally don't have billing; only show portal if there's a real Stripe customer.
    if (user.role === 'ADMIN' && !user.hasStripeCustomer) {
      return null;
    }
    // Invite-trial subscribers are SUBSCRIBER-tier but have no Stripe customer
    // record — "Manage billing" would 404 with no_customer. Fall through to
    // the Upgrade UI below so they can convert the trial into a real
    // subscription before it expires.
    if (user.role === 'SUBSCRIBER' && !user.hasStripeCustomer) {
      // continue to the Upgrade UI below
    } else {
      return (
        <>
          <button
            type="button"
            className="button button--ghost button--small"
            onClick={() => void manage()}
            disabled={busy !== null}
            title="Update payment method, view invoices, or cancel"
          >
            {busy === 'portal' ? 'Opening…' : 'Manage billing'}
          </button>
          {error && <span className="auth-bar__error" title={error}>!</span>}
        </>
      );
    }
  }

  // Free user — Upgrade chip with monthly/yearly choice in a small popover.
  return (
    <div className="upgrade-wrap">
      <button
        type="button"
        className="button button--primary button--small"
        onClick={() => setOpen((v) => !v)}
      >
        Upgrade
      </button>
      {open && (
        <div className="upgrade-pop" role="dialog">
          <div className="upgrade-pop__title">AiPrompt.si Pro</div>
          <button
            type="button"
            className="button button--primary upgrade-pop__btn"
            onClick={() => void upgrade('month')}
            disabled={busy !== null}
          >
            {busy === 'monthly' ? 'Opening…' : 'Monthly · $4.99/mo'}
          </button>
          <button
            type="button"
            className="button button--ghost upgrade-pop__btn"
            onClick={() => void upgrade('year')}
            disabled={busy !== null}
          >
            {busy === 'yearly' ? 'Opening…' : 'Yearly · $49/yr · save 18%'}
          </button>
          <ul className="upgrade-pop__list">
            <li>Unlimited generations</li>
            <li>Cloud history across devices</li>
            <li>Longer prompts (up to 2000 chars)</li>
          </ul>
          {error && <div className="modal__error">{error}</div>}
          <button
            type="button"
            className="upgrade-pop__close"
            onClick={() => setOpen(false)}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
